/**
 * Digifiy Currency System v5 — Complete unified converter
 * Fixes all bugs from v4, covers ALL price locations
 *
 * Features:
 * - Auto geo-detect country → currency
 * - Live exchange rates from API
 * - Converts ALL price elements: product pages, collections, cart, drawer, upsell, sticky bar
 * - Detects variant-change price resets and reconverts
 * - MutationObserver for AJAX cart updates and dynamic content
 * - Manual currency selector dropdown
 * - 23 currencies with correct formatting
 */
(function() {
  'use strict';

  var SHOP_CURRENCY = 'USD';
  var SUPPORTED = ['USD','EUR','GBP','CAD','AUD','AED','PKR','SEK','NOK','DKK','CHF','PLN','CZK','HUF','RON','BGN','HRK','TRY','INR','SGD','NZD','JPY','SAR'];

  var CURRENCY_INFO = {
    'USD': { symbol: '$',    format: '${{amount}}' },
    'EUR': { symbol: '€',    format: '€{{amount}}' },
    'GBP': { symbol: '£',    format: '£{{amount}}' },
    'CAD': { symbol: 'C$',   format: 'C${{amount}}' },
    'AUD': { symbol: 'A$',   format: 'A${{amount}}' },
    'AED': { symbol: '',     format: '{{amount}} AED' },
    'PKR': { symbol: 'Rs ',  format: 'Rs {{amount}}' },
    'SEK': { symbol: '',     format: '{{amount}} SEK' },
    'NOK': { symbol: '',     format: '{{amount}} NOK' },
    'DKK': { symbol: '',     format: '{{amount}} DKK' },
    'CHF': { symbol: '',     format: 'CHF {{amount}}' },
    'PLN': { symbol: '',     format: '{{amount}} PLN' },
    'CZK': { symbol: '',     format: '{{amount}} CZK' },
    'HUF': { symbol: '',     format: '{{amount}} Ft' },
    'RON': { symbol: '',     format: '{{amount}} RON' },
    'BGN': { symbol: '',     format: '{{amount}} BGN' },
    'HRK': { symbol: '',     format: '{{amount}} HRK' },
    'TRY': { symbol: '₺',   format: '₺{{amount}}' },
    'INR': { symbol: '₹',   format: '₹{{amount}}' },
    'SGD': { symbol: 'S$',   format: 'S${{amount}}' },
    'NZD': { symbol: 'NZ$',  format: 'NZ${{amount}}' },
    'JPY': { symbol: '¥',    format: '¥{{amount}}' },
    'SAR': { symbol: '',     format: '{{amount}} SAR' }
  };

  var COUNTRY_MAP = {
    'US':'USD', 'CA':'CAD',
    'GB':'GBP', 'UK':'GBP',
    'DE':'EUR', 'FR':'EUR', 'IT':'EUR', 'ES':'EUR', 'NL':'EUR',
    'BE':'EUR', 'AT':'EUR', 'PT':'EUR', 'IE':'EUR', 'FI':'EUR',
    'GR':'EUR', 'LU':'EUR', 'SK':'EUR', 'SI':'EUR', 'EE':'EUR',
    'LV':'EUR', 'LT':'EUR', 'MT':'EUR', 'CY':'EUR',
    'SE':'SEK', 'NO':'NOK', 'DK':'DKK',
    'CH':'CHF', 'PL':'PLN', 'CZ':'CZK', 'HU':'HUF',
    'RO':'RON', 'BG':'BGN', 'HR':'HRK', 'TR':'TRY',
    'AU':'AUD', 'NZ':'NZD',
    'AE':'AED', 'SA':'SAR', 'QA':'AED', 'KW':'AED', 'BH':'AED', 'OM':'AED',
    'PK':'PKR', 'IN':'INR',
    'SG':'SGD', 'JP':'JPY'
  };

  // Default rates (USD base) — overwritten by live API
  var rates = {
    'USD': 1, 'EUR': 0.92, 'GBP': 0.79, 'CAD': 1.37, 'AUD': 1.54,
    'AED': 3.67, 'PKR': 278.50, 'SEK': 10.5, 'NOK': 10.7, 'DKK': 6.88,
    'CHF': 0.88, 'PLN': 4.02, 'CZK': 23.3, 'HUF': 370, 'RON': 4.6,
    'BGN': 1.8, 'HRK': 6.9, 'TRY': 32.5, 'INR': 83.5, 'SGD': 1.34,
    'NZD': 1.65, 'JPY': 150, 'SAR': 3.75
  };

  var activeCurrency = 'USD';
  var initialized = false;

  // COMPREHENSIVE price selectors — covers ALL theme price locations
  var PRICE_SELECTORS = [
    // Core Shopify/Ella price elements
    '.price-item:not(.price-item--percent)',
    '.price-item--regular',
    '.price-item--sale',
    '.price-item--compare',
    '.price-item--last',
    '.money',
    // Product page (custom price-now/was from price.liquid)
    '.price-now',
    '.price-was',
    '.product__price',
    // Bundle pricing
    '.bundle-price',
    '.bundle-old-price',
    // Cart page
    '.cart-item__price',
    '.totals__subtotal-value',
    '.totals__total-value',
    '.cart__price',
    // Sticky add to cart bar
    '.sticky-atc__price .price-item',
    '.sticky-atc .price-item',
    // Boost.js sticky bar prices
    '.dg-price-now',
    '.dg-price-was',
    // Product cards (collection/search pages)
    '.halo-product-price',
    '.price--end',
    '.card-information .price-item',
    // Upsell widget intentionally skipped — upsell renders prices in USD directly
    // Product recommendations
    '.product-recommendations .price-item',
    // Predictive search
    '.predictive-search .price-item',
    // Compare products
    '.halo-compare-product-table .price-item',
    // Discount display
    '.discounts__discount-text .money',
    // Homepage custom sections
    '.prod-price',
    '.prod-compare',
    '.prod-save',
    '.hero-product-price',
    // Quick order list
    '.quick-order-list-total__price',
    // Variant item prices
    '.variant-item__price',
    '.variant-item__old-price'
  ].join(', ');

  // ─── EXCHANGE RATES ───
  function fetchRates(callback) {
    fetch('https://api.exchangerate-api.com/v4/latest/USD')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (data && data.rates) {
          SUPPORTED.forEach(function(c) {
            if (data.rates[c]) rates[c] = data.rates[c];
          });
          console.log('[DGF] Live rates loaded');
        }
        if (callback) callback();
      })
      .catch(function() {
        console.log('[DGF] Using fallback rates');
        if (callback) callback();
      });
  }

  // ─── PRICE PARSING ───
  // Matches: $9.99, $1,299.00, $9.99 USD, From $9.99, etc.
  function parseUSD(text) {
    if (!text) return null;
    text = text.trim();
    var m = text.match(/\$\s*([\d,]+\.?\d*)/);
    if (m) {
      var val = parseFloat(m[1].replace(/,/g, ''));
      if (val > 0) return val;
    }
    return null;
  }

  // ─── PRICE FORMATTING ───
  function formatPrice(amount, currency) {
    var info = CURRENCY_INFO[currency];
    if (!info) return '$' + amount.toFixed(2);
    var formatted;
    if (currency === 'JPY') {
      formatted = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    } else {
      formatted = amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
    return info.format.replace('{{amount}}', formatted);
  }

  // ─── CHECK IF ELEMENT SHOULD BE PROCESSED ───
  function shouldSkip(el) {
    // Skip containers that have nested price elements
    if (el.querySelector && el.querySelector('.price-item, .money, .price-now')) return true;
    // Skip percent badges
    if (el.classList && el.classList.contains('price-item--percent')) return true;
    // Skip visually hidden
    if (el.classList && el.classList.contains('visually-hidden')) return true;
    // Skip empty
    var text = el.textContent;
    if (!text || !text.trim()) return true;
    return false;
  }

  // ─── CONVERT A SINGLE ELEMENT ───
  function convertEl(el, toCurrency) {
    if (shouldSkip(el)) return false;

    var storedUSD = el.getAttribute('data-dgf-usd');
    if (!storedUSD) {
      var usd = parseUSD(el.textContent);
      if (usd === null) return false;
      el.setAttribute('data-dgf-usd', usd.toString());
      el.setAttribute('data-dgf-orig', el.innerHTML);
      storedUSD = usd.toString();
    }

    var usdVal = parseFloat(storedUSD);
    if (isNaN(usdVal) || usdVal <= 0) return false;

    if (toCurrency === 'USD') {
      var orig = el.getAttribute('data-dgf-orig');
      if (orig) el.innerHTML = orig;
    } else {
      var rate = rates[toCurrency] || 1;
      el.textContent = formatPrice(usdVal * rate, toCurrency);
    }

    el.setAttribute('data-dgf-cur', toCurrency);
    return true;
  }

  // ─── CONVERT ALL PRICES ON PAGE ───
  function convertAll(toCurrency) {
    if (!toCurrency || toCurrency === activeCurrency) return;

    var count = 0;
    document.querySelectorAll(PRICE_SELECTORS).forEach(function(el) {
      if (convertEl(el, toCurrency)) count++;
    });

    // Handle text-loader-component (cart totals use this custom element)
    document.querySelectorAll('text-loader-component').forEach(function(el) {
      if ((el.textContent || '').includes('$')) {
        if (convertEl(el, toCurrency)) count++;
      }
    });

    activeCurrency = toCurrency;
    localStorage.setItem('digifiy_currency', toCurrency);

    // Sync all dropdowns
    document.querySelectorAll('[name="currencies"]').forEach(function(dd) {
      dd.value = toCurrency;
    });

    if (count > 0) {
      console.log('[DGF] Converted ' + count + ' prices → ' + toCurrency);
    }
  }

  // ─── REVERT ALL TO USD ───
  function revertAll() {
    document.querySelectorAll('[data-dgf-usd]').forEach(function(el) {
      var orig = el.getAttribute('data-dgf-orig');
      if (orig) el.innerHTML = orig;
      el.removeAttribute('data-dgf-usd');
      el.removeAttribute('data-dgf-orig');
      el.removeAttribute('data-dgf-cur');
    });
    activeCurrency = 'USD';
    localStorage.setItem('digifiy_currency', 'USD');
    document.querySelectorAll('[name="currencies"]').forEach(function(dd) {
      dd.value = 'USD';
    });
  }

  // ─── HANDLE DYNAMIC/NEW ELEMENTS ───
  // Catches: AJAX cart updates, variant changes, upsell widgets, quick views
  function convertDynamic() {
    if (activeCurrency === 'USD') return;

    var count = 0;
    document.querySelectorAll(PRICE_SELECTORS).forEach(function(el) {
      var curAttr = el.getAttribute('data-dgf-cur');

      if (curAttr === activeCurrency) {
        // Already converted — but check if another script reset the price to USD
        // (e.g., boost.js variant change sets textContent to "$X.XX USD")
        var text = el.textContent || '';
        if (text.indexOf('$') !== -1 && parseUSD(text) !== null) {
          // Price was reset to USD by another script — reconvert
          el.removeAttribute('data-dgf-usd');
          el.removeAttribute('data-dgf-orig');
          el.removeAttribute('data-dgf-cur');
          if (convertEl(el, activeCurrency)) count++;
        }
        return;
      }

      // New element without conversion marker
      if (convertEl(el, activeCurrency)) count++;
    });

    // Also check text-loader-component
    document.querySelectorAll('text-loader-component').forEach(function(el) {
      if (el.getAttribute('data-dgf-cur') === activeCurrency) {
        var text = el.textContent || '';
        if (text.indexOf('$') !== -1 && parseUSD(text) !== null) {
          el.removeAttribute('data-dgf-usd');
          el.removeAttribute('data-dgf-orig');
          el.removeAttribute('data-dgf-cur');
          convertEl(el, activeCurrency);
          count++;
        }
        return;
      }
      if ((el.textContent || '').includes('$') && !el.getAttribute('data-dgf-cur')) {
        if (convertEl(el, activeCurrency)) count++;
      }
    });

    if (count > 0) {
      console.log('[DGF] Dynamic: ' + count + ' prices converted');
    }
  }

  // ─── GEO DETECTION ───
  function detectCountry(callback) {
    // Clear stale cache from old script versions
    if (localStorage.getItem('digifiy_v') !== '5') {
      localStorage.removeItem('digifiy_currency');
      localStorage.removeItem('digifiy_detect_time');
      localStorage.removeItem('digifiy_manual');
      sessionStorage.removeItem('digifiy_manual');
      localStorage.setItem('digifiy_v', '5');
      console.log('[DGF] Cleared stale cache from old version');
    }

    // Manual override (user selected dropdown)
    if (sessionStorage.getItem('digifiy_manual')) {
      var saved = localStorage.getItem('digifiy_currency');
      if (saved && CURRENCY_INFO[saved]) {
        callback(saved);
        return;
      }
    }

    // Returning visitor cache (24h)
    var saved = localStorage.getItem('digifiy_currency');
    var lastDetect = localStorage.getItem('digifiy_detect_time');
    if (saved && lastDetect) {
      var age = Date.now() - parseInt(lastDetect);
      if (age < 86400000 && CURRENCY_INFO[saved]) {
        callback(saved);
        return;
      }
    }

    // Detect via IP
    fetch('https://ipapi.co/json/')
      .then(function(r) {
        if (!r.ok) throw new Error('ipapi failed');
        return r.json();
      })
      .then(function(data) {
        var cc = data.country_code || data.country;
        var currency = COUNTRY_MAP[cc] || 'USD';
        console.log('[DGF] Geo: ' + cc + ' → ' + currency);
        localStorage.setItem('digifiy_detect_time', Date.now().toString());
        callback(currency);
      })
      .catch(function() {
        // Fallback API
        fetch('https://ipwho.is/')
          .then(function(r) { return r.json(); })
          .then(function(data) {
            var cc = data.country_code;
            var currency = COUNTRY_MAP[cc] || 'USD';
            console.log('[DGF] Geo (fb): ' + cc + ' → ' + currency);
            localStorage.setItem('digifiy_detect_time', Date.now().toString());
            callback(currency);
          })
          .catch(function() {
            console.log('[DGF] Geo failed → USD');
            callback('USD');
          });
      });
  }

  // ─── CURRENCY SELECTOR ───
  function setupSelector() {
    document.querySelectorAll('[name="currencies"]').forEach(function(dd) {
      dd.value = activeCurrency;

      // Clone to remove old listeners
      var fresh = dd.cloneNode(true);
      dd.parentNode.replaceChild(fresh, dd);

      fresh.addEventListener('change', function() {
        var target = this.value;
        sessionStorage.setItem('digifiy_manual', 'true');

        // Revert everything to USD first
        revertAll();

        // Then convert to target (activeCurrency is now 'USD' after revert)
        if (target !== 'USD') {
          convertAll(target);
        }
      });
    });
  }

  // ─── DOM OBSERVER ───
  function setupObserver() {
    var timer = null;
    var observer = new MutationObserver(function(mutations) {
      var dominated = false;
      for (var i = 0; i < mutations.length; i++) {
        if (mutations[i].addedNodes.length > 0) {
          dominated = true;
          break;
        }
        // Also check for text changes in price elements
        var t = mutations[i].target;
        if (t && t.parentElement) {
          try {
            if (t.parentElement.matches && t.parentElement.matches(PRICE_SELECTORS)) {
              dominated = true;
              break;
            }
          } catch(e) {}
        }
      }
      if (dominated) {
        if (timer) clearTimeout(timer);
        timer = setTimeout(convertDynamic, 350);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // ─── INIT ───
  function init() {
    if (initialized) return;
    initialized = true;

    console.log('[DGF] v5 starting...');

    fetchRates(function() {
      detectCountry(function(detected) {
        // NOTE: activeCurrency stays 'USD' here — this was the v4 bug fix!
        // convertAll will set activeCurrency to detected after conversion

        setupSelector();

        if (detected !== 'USD') {
          // First pass after small delay for page render
          setTimeout(function() { convertAll(detected); }, 300);
          // Subsequent passes for late-loading elements (upsells, recommendations, etc.)
          setTimeout(function() { convertDynamic(); }, 1500);
          setTimeout(function() { convertDynamic(); }, 3000);
          setTimeout(function() { convertDynamic(); }, 6000);
          setTimeout(function() { convertDynamic(); }, 10000);
        } else {
          activeCurrency = 'USD';
          localStorage.setItem('digifiy_currency', 'USD');
        }

        setupObserver();
        console.log('[DGF] v5 ready — ' + detected);
      });
    });
  }

  // Start
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setTimeout(init, 200);
    });
  } else {
    setTimeout(init, 200);
  }
})();

